import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ChevronRight, Info, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { ContentPageHeader } from '@/components/global';
import {
  InventoryDataTableCard,
  InventoryListFiltersCard,
  InventoryTableHead,
} from '@/components/inventory';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableCell, TableRow } from '@/components/ui/table';
import type { DashboardAlert } from '@/components/alerts';
import { cn } from '@/lib/utils';
import { notify } from '@/lib/notify';
import { formatApiErrorBody } from '@/lib/formatApiError';

type SeverityFilter = 'all' | DashboardAlert['severity'];

const severityConfig = {
  info: {
    icon: Info,
    badgeVariant: 'secondary' as const,
  },
  warning: {
    icon: AlertTriangle,
    badgeVariant: 'outline' as const,
  },
  critical: {
    icon: AlertCircle,
    badgeVariant: 'destructive' as const,
  },
};

function isDashboardAlert(value: unknown): value is DashboardAlert {
  if (!value || typeof value !== 'object') return false;
  const a = value as DashboardAlert;
  return (
    typeof a.id === 'string' &&
    typeof a.message === 'string' &&
    typeof a.link === 'string' &&
    typeof a.createdAt === 'string' &&
    (a.severity === 'info' || a.severity === 'warning' || a.severity === 'critical')
  );
}

function formatAlertTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState<SeverityFilter>('all');

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/v1/dashboard/alerts');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiErrorBody(data));
      }
      const list = Array.isArray(data.alerts) ? data.alerts : [];
      setAlerts(list.filter(isDashboardAlert));
    } catch (err) {
      setAlerts([]);
      notify.error(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  const filtered = useMemo(() => {
    if (severity === 'all') return alerts;
    return alerts.filter((a) => a.severity === severity);
  }, [alerts, severity]);

  const counts = useMemo(
    () => ({
      critical: alerts.filter((a) => a.severity === 'critical').length,
      warning: alerts.filter((a) => a.severity === 'warning').length,
      info: alerts.filter((a) => a.severity === 'info').length,
    }),
    [alerts]
  );

  return (
    <div className="space-y-6">
      <ContentPageHeader
        title="Alerts"
        description="Live operational conditions. Alerts appear while the issue is true and disappear when it clears — they are not stored or acknowledged."
        back={{ label: 'Dashboard', to: '/dashboard' }}
        actions={
          <Button variant="outline" onClick={() => void loadAlerts()} disabled={loading}>
            <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        }
      />

      <InventoryListFiltersCard>
        <Select value={severity} onValueChange={(v) => setSeverity(v as SeverityFilter)}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities ({alerts.length})</SelectItem>
            <SelectItem value="critical">Critical ({counts.critical})</SelectItem>
            <SelectItem value="warning">Warning ({counts.warning})</SelectItem>
            <SelectItem value="info">Info ({counts.info})</SelectItem>
          </SelectContent>
        </Select>
      </InventoryListFiltersCard>

      <InventoryDataTableCard
        loading={loading}
        isEmpty={filtered.length === 0}
        colSpan={4}
        emptyMessage={alerts.length === 0 ? 'No active alerts' : 'No alerts for this filter'}
        header={
          <>
            <InventoryTableHead>Severity</InventoryTableHead>
            <InventoryTableHead>Message</InventoryTableHead>
            <InventoryTableHead className="hidden sm:table-cell">Detected</InventoryTableHead>
            <InventoryTableHead className="w-[50px]" />
          </>
        }
      >
        {filtered.map((alert) => {
          const config = severityConfig[alert.severity];
          const Icon = config.icon;
          return (
            <TableRow
              key={alert.id}
              className="cursor-pointer"
              onClick={() => navigate(alert.link)}
            >
              <TableCell>
                <Badge variant={config.badgeVariant} className="capitalize">
                  <Icon className="mr-1 h-3 w-3" />
                  {alert.severity}
                </Badge>
              </TableCell>
              <TableCell className="font-medium">{alert.message}</TableCell>
              <TableCell className="hidden text-muted-foreground sm:table-cell">
                {formatAlertTime(alert.createdAt)}
              </TableCell>
              <TableCell>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </TableCell>
            </TableRow>
          );
        })}
      </InventoryDataTableCard>
    </div>
  );
}
